import { s } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives/types';

type ThemeSxObject = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;
type ThemeSxItem = Exclude<Extract<SxProps<Theme>, readonly unknown[]>[number], boolean>;

const isThemeSxItem = (sx: SxProps<Theme>): sx is ThemeSxItem => !Array.isArray(sx) && sx !== null;

type CodeOutputBlockFrameOptions = {
  showHeaderRow: boolean;
};

type CodeOutputBlockHeaderOptions = {
  hasCopyAction: boolean;
};

type CodeOutputBlockCopyButtonOptions = {
  copyIconAlignment: 'top' | 'center';
};

type CodeOutputBlockContentOptions = {
  showHeaderRow: boolean;
  hasCopyAction: boolean;
  reserveCopySpace: boolean;
  contentSx?: SxProps<Theme>;
};

export const CODE_OUTPUT_COPY_ICON_TOP_INSET = s('xs');
export const CODE_OUTPUT_COPY_ICON_RIGHT_INSET = s('xs');

export const buildCodeOutputBlockFrameSx = ({
  showHeaderRow,
}: CodeOutputBlockFrameOptions): SxProps<Theme> => ({
  px: s('lg'),
  pt: showHeaderRow ? s('lg') : s('md'),
  pb: s('md'),
  position: 'relative',
});

export const buildCodeOutputBlockHeaderSx = ({
  hasCopyAction,
}: CodeOutputBlockHeaderOptions): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: s('none'),
  marginBottom: s('md'),
  pr: hasCopyAction ? s('4xl') : undefined,
});

export const CODE_OUTPUT_BLOCK_LABEL_SX = {
  lineHeight: 1,
} as const;

export const buildCodeOutputBlockCopyButtonSx = ({
  copyIconAlignment,
}: CodeOutputBlockCopyButtonOptions): SxProps<Theme> => [
  {
    position: 'absolute',
    right: CODE_OUTPUT_COPY_ICON_RIGHT_INSET,
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  copyIconAlignment === 'center'
    ? { top: '50%', transform: 'translateY(-50%)' }
    : { top: CODE_OUTPUT_COPY_ICON_TOP_INSET },
];

export const buildCodeOutputBlockContentSx = ({
  showHeaderRow,
  hasCopyAction,
  reserveCopySpace,
  contentSx,
}: CodeOutputBlockContentOptions): SxProps<Theme> => {
  const baseContentSx: ThemeSxObject = {
    whiteSpace: 'pre-wrap',
    fontFamily: (theme) => theme.typography.mono.fontFamily,
    pr: !showHeaderRow && hasCopyAction && reserveCopySpace ? s('4xl') : undefined,
  };

  if (!contentSx) {
    return baseContentSx;
  }

  const contentSxOverrides: ThemeSxItem[] = Array.isArray(contentSx)
    ? contentSx.filter(isThemeSxItem)
    : isThemeSxItem(contentSx)
      ? [contentSx]
      : [];

  return [baseContentSx, ...contentSxOverrides];
};
